/**
 * Theme + accent preferences (design review D3/P2.4). The choice persists in
 * localStorage and is applied as `data-theme` / `data-accent` on <html> —
 * matching the token blocks in index.css. `index.html` runs a tiny inline
 * script that applies the stored theme BEFORE first paint (no flash); this
 * module is the runtime side: reading, setting, and following the OS theme
 * while the preference is "system".
 */

export type ThemePreference = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'alma-theme';
export const ACCENT_STORAGE_KEY = 'alma-accent';

/** The accent packs shipped in index.css; 'dawn' pairs Sun with Marine. */
export const ACCENT_OPTIONS = [
  { id: '', label: 'Marine', swatch: '#134473' },
  { id: 'dawn', label: 'Dawn', swatch: '#fdc65b' },
  { id: 'violet', label: 'Violet', swatch: '#8b5cf6' },
  { id: 'emerald', label: 'Emerald', swatch: '#10b981' },
  { id: 'rose', label: 'Rose', swatch: '#f43f5e' },
  { id: 'amber', label: 'Amber', swatch: '#f59e0b' },
  { id: 'sky', label: 'Sky', swatch: '#0ea5e9' },
  { id: 'indigo', label: 'Indigo', swatch: '#6366f1' },
] as const;

export function getThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Storage unavailable (private mode) — fall through to the default.
  }
  return 'light';
}

export function getAccentPreference(): string {
  try {
    return localStorage.getItem(ACCENT_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function systemPrefersDark(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Resolves a preference to the concrete data-theme value. */
export function resolveTheme(pref: ThemePreference): 'light' | 'dark' {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

/** Applies the stored (or given) preferences to <html>. */
export function applyTheme(pref: ThemePreference = getThemePreference()): void {
  document.documentElement.setAttribute('data-theme', resolveTheme(pref));
}

export function applyAccent(accent: string = getAccentPreference()): void {
  if (accent) document.documentElement.setAttribute('data-accent', accent);
  else document.documentElement.removeAttribute('data-accent');
}

export function setThemePreference(pref: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // Storage unavailable — the attribute below still applies for this session.
  }
  applyTheme(pref);
}

export function setAccentPreference(accent: string): void {
  try {
    if (accent) localStorage.setItem(ACCENT_STORAGE_KEY, accent);
    else localStorage.removeItem(ACCENT_STORAGE_KEY);
  } catch {
    // Storage unavailable — the attribute below still applies for this session.
  }
  applyAccent(accent);
}

/**
 * Applies stored preferences and keeps a "system" theme following OS changes.
 * Call once at startup; returns a cleanup for tests.
 */
export function initTheme(): () => void {
  applyTheme();
  applyAccent();
  if (typeof window.matchMedia !== 'function') return () => {};
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (getThemePreference() === 'system') applyTheme('system');
  };
  media.addEventListener?.('change', onChange);
  return () => media.removeEventListener?.('change', onChange);
}
